/**
 * J2 — „Welchem Kontakt gehört diese Mail?" als EINE ableitbare Antwort.
 *
 * Die Zuweisung entsteht an mehreren Stellen (Zuweisen-Popup, Kandidaten-Klick,
 * Kontaktanlage, Vorgangs-Zuordnung, Vorschlag bestätigen). Ein Callback je
 * Stelle würde unweigerlich einen vergessen; deshalb gibt es hier genau eine
 * Auflösung aus den vorhandenen Quellen. Rein und testbar — ohne React.
 */

export type KontaktRef = { id: number; displayName: string };

/** Ein MailLink, so weit ihn diese Auflösung braucht. */
export type LinkAehnlich = {
    messageId: string;
    contactId?: number | null;
    /** Anzeigename, falls das DTO ihn mitbringt. */
    contact?: KontaktRef | null;
    vorgang?: { id: number; titel: string; kunde?: KontaktRef | null } | null;
};

export type Zuweisungsquellen = {
    /** Die gerade geöffnete Nachricht. */
    messageId: string;
    /**
     * In DIESER Ansicht getroffene Zuweisung.
     * `undefined` = nichts entschieden, `null` = bewusst „ohne Kontakt" und
     * damit bindend (sonst käme die Erkennung sofort wieder durch).
     */
    explizit?: KontaktRef | null;
    /** Links der Konversation (persistierte Wahrheit vom Server). */
    links?: LinkAehnlich[];
    /** Erkennung aus Backend/Liste: `recognizedContact ?? matchedContact`. */
    erkannt?: KontaktRef | null;
};

export type Zuweisungsquelle = "explizit" | "link" | "vorgang" | "erkannt" | "keiner";

export type Zuweisung = {
    kontakt: KontaktRef | null;
    quelle: Zuweisungsquelle;
};

/** Ein Kontakt ohne bekannten Namen — der Reiter holt ihn bei Bedarf nach. */
function refAusId(id: number, name?: string | null): KontaktRef {
    return { id, displayName: (name ?? "").trim() || `Kontakt #${id}` };
}

/**
 * Reihenfolge (die erste Regel, die greift, gewinnt):
 *  1. explizit gesetzt (auch `null` — „ohne Kontakt" ist eine Entscheidung),
 *  2. Link DIESER Nachricht mit `contactId`,
 *  3. irgendein Link der Konversation mit `contactId`,
 *  4. Kunde eines verknüpften Vorgangs (E01),
 *  5. Erkennung,
 *  6. nichts.
 */
export function zugewiesenerKontakt(q: Zuweisungsquellen): Zuweisung {
    if (q.explizit !== undefined) return { kontakt: q.explizit, quelle: "explizit" };

    const links = q.links ?? [];
    const eigener = links.find((l) => l.messageId === q.messageId && l.contactId != null);
    if (eigener?.contactId != null) {
        return {
            kontakt: eigener.contact ?? refAusId(eigener.contactId),
            quelle: "link",
        };
    }

    const irgendein = links.find((l) => l.contactId != null);
    if (irgendein?.contactId != null) {
        return {
            kontakt: irgendein.contact ?? refAusId(irgendein.contactId),
            quelle: "link",
        };
    }

    // E01 — eine am Vorgang hängende Mail gehört automatisch zum Kunden des
    // Vorgangs. Nachrichten-eigene Links zuerst, dann die der Konversation.
    const vorgangsKunde = vorgangsKundeAus(links, q.messageId);
    if (vorgangsKunde) return { kontakt: vorgangsKunde, quelle: "vorgang" };

    if (q.erkannt) return { kontakt: q.erkannt, quelle: "erkannt" };
    return { kontakt: null, quelle: "keiner" };
}

/**
 * Der Kunde des Vorgangs, an dem diese Nachricht (oder sonst ihre
 * Konversation) hängt — nachrichten-eigene Links zuerst. `null`, wenn keiner.
 *
 * T-22: Beim Antworten „in einem Vorgang" ist er der Kontext für die
 * Empfänger-Vorschläge — wichtiger als der erkannte Absender, der bei Mails
 * aus einem gemeinsamen Postfach oft fehlt.
 */
export function vorgangsKundeAus(
    links: readonly LinkAehnlich[] | null | undefined,
    messageId: string,
): KontaktRef | null {
    const liste = links ?? [];
    const mitKunde =
        liste.find((l) => l.messageId === messageId && l.vorgang?.kunde) ??
        liste.find((l) => l.vorgang?.kunde);
    return mitKunde?.vorgang?.kunde ?? null;
}

/**
 * T-225/T-256 — der Vorgang, an dem diese Nachricht (oder sonst ihre
 * Konversation) hängt; nachrichten-eigene Links zuerst. `null`, wenn keiner.
 *
 * Anders als `vorgangsKundeAus` zählt hier JEDER Vorgangslink, auch einer
 * ohne Kunden: Beim Antworten sollen die Dokumente und Kontakte genau dieses
 * Vorgangs angeboten werden, ob er einen Kunden hat oder nicht.
 */
export function vorgangAus(
    links: readonly LinkAehnlich[] | null | undefined,
    messageId: string,
): { id: number; titel: string; kunde: KontaktRef | null } | null {
    const liste = links ?? [];
    const treffer =
        liste.find((l) => l.messageId === messageId && l.vorgang) ?? liste.find((l) => l.vorgang);
    const v = treffer?.vorgang;
    return v ? { id: v.id, titel: v.titel, kunde: v.kunde ?? null } : null;
}

/** Ein direkter Kontakt-Link der geöffneten Nachricht, soweit die Leiste ihn braucht. */
export type KontaktLinkAehnlich = LinkAehnlich & { id: number };

/**
 * „zuweisen mehrere Firmen/Kontakte bei Mails" (PO 28.09.2026) — die WEITEREN
 * Kontakte, denen genau diese Nachricht zugewiesen ist, neben dem angezeigten
 * Hauptkontakt.
 *
 * Nur direkte Kontakt-Links (`contactId`) DIESER Nachricht zählen — der Kunde
 * eines verknüpften Vorgangs ist keine eigene Zuweisung. Je Kontakt EIN
 * Eintrag (der erste Link gewinnt, er trägt die Id zum Entfernen). Rein, ohne
 * React.
 */
export function weitereKontaktZuweisungen(
    links: readonly KontaktLinkAehnlich[] | null | undefined,
    messageId: string,
    hauptKontaktId: number | null | undefined,
): Array<{ linkId: number; kontakt: KontaktRef }> {
    const out: Array<{ linkId: number; kontakt: KontaktRef }> = [];
    const gesehen = new Set<number>();
    for (const l of links ?? []) {
        if (l.messageId !== messageId || l.contactId == null) continue;
        if (l.contactId === hauptKontaktId || gesehen.has(l.contactId)) continue;
        gesehen.add(l.contactId);
        out.push({ linkId: l.id, kontakt: l.contact ?? refAusId(l.contactId) });
    }
    return out;
}
